import stackStyles from '../components/sections/stack-section/StackList.module.css'
import type {StackSectionEntryType, StackSectionEntryStatus} from './merge-box-status-calculator/stack-section-status'
import type {
  StackRebasePullRequestStatus,
  StackRebasePullRequestEntry,
} from '../hooks/mutations/use-stack-rebase-mutation'

// Each status maps to a "tone" — a Primer color bucket. The vertical timeline
// line segment between two icons is drawn in the upper entry's tone so the
// line, icon, and label below it all read as the same status (per
// `line-colors.png`).
type LineTone = 'default' | 'muted' | 'attention' | 'danger' | 'success' | 'done'

const STATUS_TONE: Record<StackSectionEntryStatus, LineTone> = {
  IRRELEVANT_MERGED: 'done',
  IRRELEVANT_AFTER: 'default',
  IRRELEVANT_DRAFT: 'muted',
  IRRELEVANT_QUEUED: 'attention',
  BLOCKED_STACK: 'attention',
  OK: 'success',
  LOADING_MERGE_STATE: 'default',
  ERROR_MERGE_STATE: 'attention',
  BLOCKED_MERGE_STATE: 'danger',
  UNKNOWN_MERGE_STATE: 'default',
  WAITING_ON_PREVIOUS: 'attention',
  AWAITING_APPROVAL: 'danger',
  MERGING: 'done',
  CHECKS_PENDING: 'attention',
  CHECKS_FAILING: 'danger',
  FAILING_RULES: 'danger',
  MERGE_CONFLICTS: 'danger',
}

// Tone classes set the corresponding CSS custom property on the `<li>` so the
// `::before` and inner `<a>::after` line segments can pick it up. Two parallel
// class families (above/below) let each segment between two icons match the
// upper entry's color end-to-end (rather than splitting at the icon).
const LINE_ABOVE_CLASS: Record<LineTone, string> = {
  default: stackStyles.lineAboveDefault,
  muted: stackStyles.lineAboveMuted,
  attention: stackStyles.lineAboveAttention,
  danger: stackStyles.lineAboveDanger,
  success: stackStyles.lineAboveSuccess,
  done: stackStyles.lineAboveDone,
}

const LINE_BELOW_CLASS: Record<LineTone, string> = {
  default: stackStyles.lineBelowDefault,
  muted: stackStyles.lineBelowMuted,
  attention: stackStyles.lineBelowAttention,
  danger: stackStyles.lineBelowDanger,
  success: stackStyles.lineBelowSuccess,
  done: stackStyles.lineBelowDone,
}

function entryTone(entry: StackSectionEntryType, rebaseState?: StackRebasePullRequestStatus): LineTone {
  if (rebaseState === 'PENDING') return 'default'
  if (rebaseState === 'SUCCEEDED') return 'success'
  if (rebaseState === 'CONFLICTED' || rebaseState === 'FAILED') return 'attention'
  return STATUS_TONE[entry.status]
}

// Returns the className(s) to apply to a stack entry's `NavList.Item` so its
// vertical line segments above and below the icon match the design.
//
// The line above this entry's icon belongs to (and matches the color of) the
// previous entry, so the segment between two icons reads as one continuous
// color (the upper entry's). The first entry has no line above (hidden via
// `:first-child::before` in CSS) so the fallback there doesn't matter.
//
// We use className (not inline style) because Primer `NavList.Item` routes
// `className` to the `<li>` while `style` lands on the inner `<a>`; the line
// pseudo-elements are on the `<li>` and only see custom properties set there.
export function getStackEntryLineClass(
  entry: StackSectionEntryType,
  rebaseState?: StackRebasePullRequestStatus,
  previousEntry?: StackSectionEntryType,
  previousRebaseState?: StackRebasePullRequestStatus,
): string {
  const below = entryTone(entry, rebaseState)
  const above = previousEntry ? entryTone(previousEntry, previousRebaseState) : below
  return `${LINE_ABOVE_CLASS[above]} ${LINE_BELOW_CLASS[below]}`
}

// Returns the className(s) to apply to the base reference name item (`<li>`) in the stack.
// Uses the last entry in the stack to determine the appropriate line color.
export function getBaseRefLineClass(
  entries: StackSectionEntryType[],
  rebaseDisplayState: Record<number, StackRebasePullRequestEntry>,
): string {
  if (entries.length === 0) return ''

  const lastEntry = entries[entries.length - 1]!
  const previousEntry = entries.length > 1 ? entries[entries.length - 2] : undefined

  return getStackEntryLineClass(
    lastEntry,
    rebaseDisplayState[lastEntry.pull.number]?.state,
    previousEntry,
    previousEntry && rebaseDisplayState[previousEntry.pull.number]?.state,
  )
}
